import { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search, Plus, Layers, Pencil, Trash2, CheckCircle2, XCircle, ShoppingBag, UploadCloud, RotateCcw, X } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select, { Pagination } from '../../common/Select/Select'
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  clearCategoryError,
} from './services/categorySlice'

const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#A855F7']

const STATUS_OPTIONS = [
  { value: 1, label: 'active' },
  { value: 0, label: 'inactive' },
]

const makeSlug = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

const EMPTY_FORM = {
  name: '',
  slug: '',
  parent_id: '',
  description: '',
  color: '#4F46E5',
  status: 1,
  sort_order: 0,
  is_featured: false,
  is_customizable: false,
  allow_logo_upload: false,
  allow_text_print: false,
  allow_color_choice: false,
  min_order_qty: 1,
  lead_time_days: 0,
  meta_title: '',
  meta_description: '',
  image: null,
  banner_image: null,
  remove_image: false,
  remove_banner: false,
}

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/* ── Image upload (dropzone + preview) ── */
function ImageUpload({ label, file, existingUrl, removed, onPick, onRemoveExisting, onUndoRemove, hint, wide }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const [preview, setPreview] = useState(null)

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return undefined
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const pick = (f) => {
    if (f && f.type.startsWith('image/')) onPick(f)
  }

  const openDialog = () => inputRef.current?.click()

  const showExisting = !file && existingUrl && !removed
  const showRemoved = !file && existingUrl && removed
  const shownSrc = file ? preview : showExisting ? existingUrl : null

  return (
    <>
      <label>{label}</label>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          pick(e.target.files[0])
          e.target.value = ''
        }}
      />

      {shownSrc ? (
        <div className={`upl-preview ${wide ? 'wide' : ''}`}>
          <img src={shownSrc} alt={label} />
          <div className="upl-overlay">
            <button type="button" className="upl-btn" onClick={openDialog}>
              <UploadCloud size={13} /> Replace
            </button>
            <button
              type="button"
              className="upl-btn danger"
              onClick={() => (file ? onPick(null) : onRemoveExisting())}
            >
              <X size={13} /> Remove
            </button>
          </div>
          <div className="upl-meta">
            <span className="upl-name">{file ? file.name : 'Current image'}</span>
            {file && <span className="upl-size">{formatSize(file.size)}</span>}
          </div>
        </div>
      ) : showRemoved ? (
        <div className="upl-removed">
          <span>Current image will be removed</span>
          <button type="button" className="upl-undo" onClick={onUndoRemove}>
            <RotateCcw size={12} /> Undo
          </button>
        </div>
      ) : (
        <div
          className={`upl-drop ${wide ? 'wide' : ''} ${dragging ? 'dragging' : ''}`}
          role="button"
          tabIndex={0}
          onClick={openDialog}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openDialog()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            pick(e.dataTransfer.files[0])
          }}
        >
          <span className="upl-icon"><UploadCloud size={20} /></span>
          <span className="upl-title"><b>Click to upload</b> or drag &amp; drop</span>
          <span className="upl-hint">{hint}</span>
        </div>
      )}
    </>
  )
}

export default function Category() {
  const dispatch = useDispatch()
  const { list: categories, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.category)

  const [search, setSearch] = useState('')

  // ── Main Category / Sub Category filters ──
  const [mainCategoryFilter, setMainCategoryFilter] = useState('') // '' = All
  const [subCategoryFilter, setSubCategoryFilter] = useState('')   // '' = All

  // ── Pagination ──
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10) // default 10

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    dispatch(fetchCategories())
  }, [dispatch])

  // Main category dropdown: fakt top-level categories (parent_id nathi tevi)
  const mainCategories = useMemo(
    () => categories.filter((c) => !c.parent_id),
    [categories]
  )

  // Sub category dropdown: jo main category pasand karyu hoy to tena j children,
  // nahi to badhi sub-categories (jena parent_id chhe te badhi)
  const subCategoryOptions = useMemo(() => {
    if (mainCategoryFilter) {
      return categories.filter((c) => String(c.parent_id) === String(mainCategoryFilter))
    }
    return categories.filter((c) => c.parent_id)
  }, [categories, mainCategoryFilter])

  // ── Custom Select options ──
  const mainSelectOptions = useMemo(
    () => [{ value: '', label: 'All Main Categories' }, ...mainCategories.map((c) => ({ value: c.id, label: c.name }))],
    [mainCategories]
  )

  const subSelectOptions = useMemo(
    () => [{ value: '', label: 'All Sub Categories' }, ...subCategoryOptions.map((c) => ({ value: c.id, label: c.name }))],
    [subCategoryOptions]
  )

  const parentSelectOptions = useMemo(
    () => [
      { value: '', label: '— None (Main category) —' },
      // fakt main categories (parent_id nathi tevi) — pote ne parent na banavi shakay
      ...mainCategories
        .filter((c) => !active || c.id !== active.id)
        .map((c) => ({ value: c.id, label: c.name })),
    ],
    [mainCategories, active]
  )

  // Main category badlay tyare, jooni sub category pasandgi reset karo
  // (karan ke te have selected main na children ma na pan hoy)
  const handleMainCategoryChange = (value) => {
    setMainCategoryFilter(value)
    setSubCategoryFilter('')
  }

  // latest first (newest id upar) - create/update pachhi pan same order rahe
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => Number(b.id) - Number(a.id)),
    [categories]
  )

  const filtered = sortedCategories.filter((c) => {
    // ---- text search ----
    const matchesSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.slug.toLowerCase().includes(search.toLowerCase())
    if (!matchesSearch) return false

    // ---- Sub Category filter: matra e ek j sub-category dekhado ----
    if (subCategoryFilter) {
      return String(c.id) === String(subCategoryFilter)
    }

    // ---- Main Category filter: e category + teni badhi sub-categories dekhado ----
    if (mainCategoryFilter) {
      return String(c.id) === String(mainCategoryFilter) || String(c.parent_id) === String(mainCategoryFilter)
    }

    // ---- Koi filter na hoy to badhu dekhado ----
    return true
  })

  // filter / search badlay tyare pehla page par pacha jao
  useEffect(() => {
    setPage(1)
  }, [search, mainCategoryFilter, subCategoryFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    setPage(1)
  }

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))

  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const openAdd = () => {
    dispatch(clearCategoryError())
    setActive(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (cat) => {
    dispatch(clearCategoryError())
    setActive(cat)
    setForm({
      ...EMPTY_FORM,
      name: cat.name,
      slug: cat.slug,
      parent_id: cat.parent_id || '',
      description: cat.description || '',
      color: cat.color,
      status: cat.status,
      sort_order: cat.sort_order,
      is_featured: cat.is_featured,
      is_customizable: cat.is_customizable,
      allow_logo_upload: cat.allow_logo_upload,
      allow_text_print: cat.allow_text_print,
      allow_color_choice: cat.allow_color_choice,
      min_order_qty: cat.min_order_qty,
      lead_time_days: cat.lead_time_days,
      meta_title: cat.meta_title || '',
      meta_description: cat.meta_description || '',
    })
    setModalOpen(true)
  }

  const buildFormData = () => {
    const fd = new FormData()

    // edit hoy to id body ma moklo
    if (active) fd.append('id', active.id)

    Object.entries(form).forEach(([key, val]) => {
      if (key === 'image' || key === 'banner_image') {
        if (val instanceof File) fd.append(key, val)
      } else if (typeof val === 'boolean') {
        fd.append(key, val ? 1 : 0)
      } else if (val !== null && val !== undefined) {
        fd.append(key, val)
      }
    })
    return fd
  }

  const save = async () => {
    if (!form.name.trim()) return
    const fd = buildFormData()

    const result = active
      ? await dispatch(updateCategory(fd))
      : await dispatch(createCategory(fd))

    if (result.meta.requestStatus === 'fulfilled') {
      setModalOpen(false)
    }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteCategory(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  const total = categories.length
  const activeCount = categories.filter((c) => c.status === 1).length
  const inactiveCount = categories.filter((c) => c.status === 0).length
  const productCount = categories.reduce((sum, c) => sum + Number(c.products_count || 0), 0)

  const stats = [
    { label: 'Total Categories', value: total, icon: Layers, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', variant: 'indigo' },
    { label: 'Active', value: activeCount, icon: CheckCircle2, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', variant: 'green' },
    { label: 'Inactive', value: inactiveCount, icon: XCircle, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', variant: 'red' },
    { label: 'Total Products', value: productCount, icon: ShoppingBag, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', variant: 'amber' },
  ]

  return (
    <div className="dash-page page-in">
      {/* ── PAGE HEADER ── */}
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Categories</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Category</button>
      </div>

      {/* ── STATS ── */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position: 'absolute', top: 0, right: 0, width: 110, height: 110,
              borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
              transform: 'translate(30%, -30%)', pointerEvents: 'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.value}
              </span>
            </div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      {/* ── TABLE ── */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label className="tb-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search categories…"
              aria-label="Search categories"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          {/* ── Main Category filter ── */}
          <Select
            className="cat-filter-select"
            ariaLabel="Main category filter"
            value={mainCategoryFilter}
            onChange={handleMainCategoryChange}
            options={mainSelectOptions}
            searchable={mainSelectOptions.length > 8}
            minMenuWidth={220}
          />

          {/* ── Sub Category filter ── */}
          <Select
            className="cat-filter-select"
            ariaLabel="Sub category filter"
            value={subCategoryFilter}
            onChange={setSubCategoryFilter}
            options={subSelectOptions}
            searchable={subSelectOptions.length > 8}
            minMenuWidth={220}
          />

          {(mainCategoryFilter || subCategoryFilter || search) && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setSearch('')
                setMainCategoryFilter('')
                setSubCategoryFilter('')
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category ID</th><th>Category</th><th>Slug</th>
                <th>Products</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No categories found</td></tr>
              )}
              {paginated.map((cat) => (
                <tr key={cat.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>
                    CAT-{String(cat.id).padStart(3, '0')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      {cat.image ? (
                        <img
                          src={cat.image}
                          alt={cat.name}
                          style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: 'var(--radius-md)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          background: `${cat.color}22`, color: cat.color, border: `1px solid ${cat.color}44`,
                        }}>
                          <Layers size={15} />
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text1)' }}>
                          {cat.name}
                          {cat.is_customizable && (
                            <span className="badge badge-gold" style={{ marginLeft: 6 }}>Customizable</span>
                          )}
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                          {cat.parent_name ? `${cat.parent_name} › ` : ''}{cat.description || '—'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{cat.slug}</td>
                  <td><span className="badge badge-gold">{cat.products_count} items</span></td>
                  <td>
                    <span className={`badge ${cat.status === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {cat.status === 1 ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(cat)}><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(cat); setDeleteOpen(true) }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── PAGINATION ── */}
        {!isLoading && filtered.length > 0 && (
          <Pagination
            total={filtered.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      <Modal
        open={modalOpen}
        title={active ? 'Edit Category' : 'Add Category'}
        subtitle="Organize your product catalog"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : active ? 'Update Category' : 'Create Category'}
            </button>
          </>
        )}
      >
        {isError && message && (
          <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>
        )}

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Category Name</label>
            <input
              className="input" placeholder="e.g. Custom Pen" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: active ? form.slug : makeSlug(e.target.value) })}
            />
            {fieldError('name')}
          </div>
          <div className="form-field">
            <label>Slug</label>
            <input
              className="input" placeholder="custom-pen" value={form.slug}
              onChange={(e) => setField('slug', makeSlug(e.target.value))}
            />
            {fieldError('slug')}
          </div>

          <div className="form-field">
            <label>Parent Category</label>
            <Select
              value={form.parent_id}
              onChange={(v) => setField('parent_id', v)}
              options={parentSelectOptions}
              searchable={parentSelectOptions.length > 8}
              placeholder="— None (Main category) —"
            />
            {fieldError('parent_id')}
          </div>
          <div className="form-field">
            <label>Sort Order</label>
            <input
              type="number" className="input" value={form.sort_order}
              onChange={(e) => setField('sort_order', e.target.value)}
            />
            {fieldError('sort_order')}
          </div>

          <div className="form-field full">
            <label>Description</label>
            <textarea
              className="input" rows={3} placeholder="Short description" value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>

          <div className="form-field">
            <ImageUpload
              label="Category Image"
              hint="PNG, JPG or WEBP"
              file={form.image}
              existingUrl={active?.image}
              removed={form.remove_image}
              onPick={(f) => setForm((s) => ({ ...s, image: f, remove_image: f ? false : s.remove_image }))}
              onRemoveExisting={() => setField('remove_image', true)}
              onUndoRemove={() => setField('remove_image', false)}
            />
            {fieldError('image')}
          </div>
          <div className="form-field">
            <ImageUpload
              wide
              label="Banner Image"
              hint="Wide image, e.g. 1200 × 400"
              file={form.banner_image}
              existingUrl={active?.banner_image}
              removed={form.remove_banner}
              onPick={(f) => setForm((s) => ({ ...s, banner_image: f, remove_banner: f ? false : s.remove_banner }))}
              onRemoveExisting={() => setField('remove_banner', true)}
              onUndoRemove={() => setField('remove_banner', false)}
            />
            {fieldError('banner_image')}
          </div>

          <div className="form-field">
            <label>Color</label>
            <div className="cat-colors">
              {COLORS.map((c) => (
                <button
                  key={c} type="button"
                  className={`cat-color ${form.color === c ? 'selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => setField('color', c)}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <div className="form-field">
            <label>Status</label>
            <Select
              value={form.status}
              onChange={(v) => setField('status', Number(v))}
              options={STATUS_OPTIONS}
            />
          </div>

          {/* ---- Customization settings ---- */}
          <div className="form-field full">
            <label style={{ fontWeight: 600 }}>Customization Options</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <label><input type="checkbox" checked={form.is_customizable} onChange={(e) => setField('is_customizable', e.target.checked)} /> Customizable</label>
              <label><input type="checkbox" checked={form.allow_logo_upload} onChange={(e) => setField('allow_logo_upload', e.target.checked)} /> Logo upload</label>
              <label><input type="checkbox" checked={form.allow_text_print} onChange={(e) => setField('allow_text_print', e.target.checked)} /> Text / name print</label>
              <label><input type="checkbox" checked={form.allow_color_choice} onChange={(e) => setField('allow_color_choice', e.target.checked)} /> Color choice</label>
              <label><input type="checkbox" checked={form.is_featured} onChange={(e) => setField('is_featured', e.target.checked)} /> Show on home page</label>
            </div>
          </div>
          <div className="form-field">
            <label>Min Order Qty</label>
            <input
              type="number" min={1} className="input" value={form.min_order_qty}
              onChange={(e) => setField('min_order_qty', e.target.value)}
            />
            {fieldError('min_order_qty')}
          </div>
          <div className="form-field">
            <label>Lead Time (days)</label>
            <input
              type="number" min={0} className="input" value={form.lead_time_days}
              onChange={(e) => setField('lead_time_days', e.target.value)}
            />
            {fieldError('lead_time_days')}
          </div>

          {/* ---- SEO ---- */}
          <div className="form-field full">
            <label>Meta Title</label>
            <input
              className="input" value={form.meta_title}
              onChange={(e) => setField('meta_title', e.target.value)}
            />
          </div>
          <div className="form-field full">
            <label>Meta Description</label>
            <textarea
              className="input" rows={2} value={form.meta_description}
              onChange={(e) => setField('meta_description', e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete category"
        message={`Delete ${active?.name || 'selected category'}? Products in this category will become uncategorized.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        textarea.input { resize: vertical; min-height: 70px; }

        /* ---- page header (title left, action right) ---- */
        .pg-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-md); flex-wrap: wrap;
        }

        /* ---- toolbar search (stretches across the toolbar) ---- */
        .tb-search {
          flex: 1 1 260px; min-width: 0; margin: 0;
          display: flex; align-items: center; gap: 10px;
          height: 38px; padding: 0 12px; cursor: text;
          border: 1px solid var(--border); border-radius: 10px;
          background: #f6f8ff; color: var(--text3);
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tb-search:focus-within {
          border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16);
        }
        .tb-search svg { flex-shrink: 0; }
        .tb-search input {
          flex: 1; min-width: 0; height: 100%; padding: 0; margin: 0;
          border: 0; outline: 0; box-shadow: none; background: transparent;
          font: inherit; font-size: var(--fs-sm); color: var(--text1);
        }
        .tb-search input::placeholder { color: var(--text3); }

        .cat-colors { display: flex; gap: 8px; align-items: center; height: 36px; }
        .cat-color {
          width: 24px; height: 24px; border-radius: 50%;
          border: 2px solid transparent; cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .cat-color:hover { transform: scale(1.12); }
        .cat-color.selected {
          border-color: var(--text1);
          box-shadow: 0 0 0 2px var(--bg-surface);
        }
        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5;
          font-size: 0.875rem;
          padding: 0.6rem 0.9rem;
          border-radius: 10px;
        }

        /* ── Category filters (custom Select trigger) ── */
        .sel-trigger.cat-filter-select {
          width: auto;
          min-width: 180px;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .sel-trigger.cat-filter-select { min-width: 140px; flex: 1; }
        }

        /* ── Image upload ── */
        .upl-drop {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 4px; text-align: center;
          height: 132px; padding: var(--space-md);
          border: 1.5px dashed var(--border-active);
          border-radius: var(--radius-md);
          background: var(--bg-raised);
          cursor: pointer; outline: none;
          transition: border-color var(--ease-base), background var(--ease-base), transform var(--ease-base);
        }
        .upl-drop:hover, .upl-drop:focus-visible {
          border-color: var(--accent);
          background: var(--bg-hover);
        }
        .upl-drop.dragging {
          border-color: var(--accent);
          background: var(--bg-active);
          transform: scale(1.01);
        }
        .upl-icon {
          width: 38px; height: 38px; border-radius: 50%;
          display: grid; place-items: center;
          color: var(--accent-hover);
          background: var(--accent-glow-sm);
          border: 1px solid var(--border);
          margin-bottom: 4px;
          transition: transform var(--ease-base);
        }
        .upl-drop:hover .upl-icon { transform: translateY(-2px); }
        .upl-title { font-size: var(--fs-sm); color: var(--text2); }
        .upl-title b { color: var(--accent-hover); font-weight: 600; }
        .upl-hint { font-size: var(--fs-xs); color: var(--text3); }

        .upl-preview {
          position: relative; height: 132px;
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
          background: var(--bg-raised);
          overflow: hidden;
        }
        .upl-preview img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform var(--ease-slow);
        }
        .upl-preview:hover img { transform: scale(1.04); }
        .upl-overlay {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          background: rgba(2, 6, 23, .55);
          backdrop-filter: blur(2px);
          opacity: 0; transition: opacity var(--ease-base);
        }
        .upl-preview:hover .upl-overlay,
        .upl-preview:focus-within .upl-overlay { opacity: 1; }
        .upl-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: var(--font-body); font-size: var(--fs-xs); font-weight: 600;
          padding: 6px 12px; border-radius: var(--radius-full);
          background: rgba(255,255,255,.14); color: #fff;
          border: 1px solid rgba(255,255,255,.28);
          cursor: pointer; transition: background var(--ease-fast), transform var(--ease-fast);
        }
        .upl-btn:hover { background: rgba(255,255,255,.26); transform: translateY(-1px); }
        .upl-btn.danger { background: rgba(239,68,68,.35); border-color: rgba(239,68,68,.6); }
        .upl-btn.danger:hover { background: rgba(239,68,68,.55); }
        .upl-meta {
          position: absolute; left: 0; right: 0; bottom: 0;
          display: flex; justify-content: space-between; align-items: center; gap: 8px;
          padding: 6px 10px;
          font-size: var(--fs-xs); color: #fff;
          background: linear-gradient(0deg, rgba(2,6,23,.75), transparent);
          pointer-events: none;
        }
        .upl-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .upl-size { flex-shrink: 0; opacity: .8; font-family: var(--font-mono); }

        .upl-removed {
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
          height: 132px; padding: 0 var(--space-md);
          border: 1.5px dashed rgba(239,68,68,.4);
          border-radius: var(--radius-md);
          background: rgba(239,68,68,.06);
          color: var(--color-red); font-size: var(--fs-sm);
        }
        .upl-undo {
          display: inline-flex; align-items: center; gap: 4px;
          font-family: var(--font-body); font-size: var(--fs-xs); font-weight: 600;
          padding: 5px 10px; border-radius: var(--radius-full);
          background: transparent; color: var(--text1);
          border: 1px solid var(--border-active); cursor: pointer;
        }
        .upl-undo:hover { background: var(--bg-hover); }

        /* touch devices: overlay hover nathi hotu, etle buttons hamesha dekhay */
        @media (hover: none) {
          .upl-overlay { opacity: 1; background: rgba(2, 6, 23, .35); align-items: flex-start; padding-top: 8px; }
        }
      `}</style>
    </div>
  )
}